/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Mounts its children on the first `open` and keeps them mounted after: the host
 * of a lazy modal (lazyModals.ts). Closed and never opened, it renders nothing,
 * so the modal's chunk is not even requested; at SSR `open` is false, so nothing
 * of a modal is in the baked HTML — as before the modals went lazy.
 *
 * Why it stays mounted once opened: the modals animate out through
 * AnimatePresence, and that exit only runs while the modal is still in the tree;
 * unmounting on `open=false` would cut the close animation off. The modals
 * unmount their own panel on close (that IS their reset), so only this shell
 * stays.
 *
 * The Suspense boundary has no fallback: until the chunk is in there is nothing
 * to show, and the trigger preloads on hover/focus/touch so the wait is usually
 * zero. Pinned by src/components/__tests__/LazyOnOpen.test.tsx.
 */

import React, { Suspense, useState } from 'react';

interface LazyOnOpenProps {
  open: boolean;
  children: React.ReactNode;
}

export const LazyOnOpen: React.FC<LazyOnOpenProps> = ({ open, children }) => {
  const [mounted, setMounted] = useState(open);
  // A render-phase update of the component's OWN state is allowed and means the
  // children render in the same pass as the `open` that asked for them, not a
  // frame later.
  if (open && !mounted) setMounted(true);
  if (!mounted) return null;
  return <Suspense fallback={null}>{children}</Suspense>;
};
